<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  completeOrder,
  getOrder,
  getOrderItems,
  getOrderSettings,
  updateOrderComment,
  validateOrder,
} from '../../services/orderApi'
import { OrderInterface, OrderType } from '../../models/OrderModel'
import ALoader from '../common/ALoader.vue'
import { parseBadgeClass, parseStatus, parseType } from '../../models/helpers'
import AButton from '../common/AButton.vue'
import router from '../../router'
import { useI18n } from 'vue-i18n'
import { showToast } from '../../services/toast'
import { Modal } from 'bootstrap'
import { useRoute } from 'vue-router'
import { isNetworkFailure } from '../../services/https'
import ATextarea from '../common/ATextarea.vue'
import { ItemInterface } from '../../models/ItemModel'

const props = defineProps<{ id: string }>()
const isLoading = ref<boolean>(true)
const isSubmitting = ref<boolean>(false)
const order = ref<OrderInterface | null>(null)
const items = ref<ItemInterface[]>([])
const { t } = useI18n()
const modalEl = ref<HTMLElement | null>(null)
let modalInstance: typeof Modal | null = null
const orderError = ref<string | null>(null)
const route = useRoute()
const showRetry = ref<boolean>(false)
const comment = ref<string>('')
type BsModal = InstanceType<typeof Modal>
const commentModalRef = ref<HTMLElement | null>(null)
const commentModal = ref<BsModal | null>(null)
const showItems = ref<boolean>(false)
const enableReservation = ref<boolean>(false)
const enableCreateOffer = ref<boolean>(false)
const reservationMode = ref<boolean>(false)
const offerMode = ref<boolean>(false)
const totalItems = ref<number | null>(null)

const operationType: string = String(route.query.type) || 'default'

onMounted(() => {
  getOrderSettings().then((parsedSettings) => {
    enableReservation.value = parsedSettings.enableReservation || false
    enableCreateOffer.value = parsedSettings.enableAddProducts || false
  })
  getOrder(props.id)
    .then((parsedOrder) => {
      order.value = parsedOrder
      comment.value = order.value?.info || ''
      calculateTotalItems()
    })
    .finally(() => {
      isLoading.value = false
    })
  getOrderItems(props.id)
    .then((parsedItems) => {
      items.value = parsedItems
    })
    .finally(() => {
      isLoading.value = false
    })
  if (commentModalRef.value) {
    commentModal.value = new Modal(commentModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }
})

onBeforeUnmount(() => {
  commentModal.value?.dispose()
})

function calculateTotalItems() {
  let itemCount = 0
  items.value.forEach((item) => {
    itemCount += item.quantity_total || 0
  })
  totalItems.value = itemCount
}

watch(items, () => {
  calculateTotalItems()
})

function parseStartOperationLabel(): string {
  switch (operationType) {
    case 'inbound':
      return t('inbound-start')
    case 'outbound':
      return t('outbound-start')
    case 'inventory':
      return t('inventory-start')
    case 'backorder':
      return t('backorder-start')
    case 'order':
      return t('order-start')
    default:
      return t('operation-start')
  }
}

function parseCompleteOperationLabel(): string {
  switch (operationType) {
    case 'inbound':
      return t('inbound-complete')
    case 'outbound':
      return t('outbound-complete')
    case 'inventory':
      return t('inventory-complete')
    case 'backorder':
      return t('backorder-complete')
    case 'order':
      return t('order-complete')
    default:
      return t('operation-complete')
  }
}

function openModal(isReservation = false, isOffer = false) {
  if (!modalEl.value) return

  if (!modalInstance) {
    modalInstance = new Modal(modalEl.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  reservationMode.value = isReservation
  offerMode.value = isOffer

  modalInstance.show()
}

function goToItems() {
  router.push({ name: 'order-processing', params: { id: props.id } })
}

async function validateCurrentOrder(isReservation = false, isOffer = false) {
  isSubmitting.value = true
  orderError.value = null
  validateOrder(props.id)
    .then((parsedValidationResponse) => {
      if (parsedValidationResponse.type === 'success') {
        showToast({ message: t('validate-order-success'), variant: 'success', noTitle: true })
      } else {
        orderError.value = parsedValidationResponse.message
      }
      openModal(isReservation, isOffer)
      return
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

async function completeCurrentOrder() {
  isSubmitting.value = true
  showRetry.value = false
  completeOrder(props.id, reservationMode.value, offerMode.value)
    .then(() => {
      showToast({
        message: t(getSuccessText()),
        variant: 'success',
        noTitle: true,
      })
      if (modalInstance) {
        modalInstance.hide()
      }
      router.push({ name: 'dashboard' })
    })
    .catch((error) => {
      if (isNetworkFailure(error)) {
        showRetry.value = true
      }
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

function openCommentModal() {
  commentModal.value?.show()
}

function closeCommentModal() {
  commentModal.value?.hide()
}

function getModalHeader() {
  if (reservationMode.value) {
    return t('finish-reservation-header')
  } else if (offerMode.value) {
    return t('finish-offer-header')
  } else {
    return t('finish-order-header')
  }
}

function getModalText() {
  if (reservationMode.value) {
    return t('finish-reservation-text')
  } else if (offerMode.value) {
    return t('finish-offer-text')
  } else {
    return t('finish-order-text')
  }
}

function getSuccessText() {
  if (reservationMode.value) {
    return t('complete-reservation-success')
  } else if (offerMode.value) {
    return t('complete-offer-success')
  } else {
    return t('complete-order-success')
  }
}

async function updateComment(): Promise<void> {
  isSubmitting.value = true
  updateOrderComment(props.id, comment.value)
    .then(() => {
      order.value.info = comment.value
      showToast({ message: t('order-comment-update-success'), variant: 'success', noTitle: true })
      closeCommentModal()
    })
    .finally(() => {
      isSubmitting.value = false
    })
}
</script>

<template>
  <div class="h-100 d-flex flex-column mt--12">
    <ALoader v-if="isLoading" />
    <template v-else-if="order">
      <div class="order-info mb-24">
        <div class="table-responsive position-relative">
          <table>
            <tbody>
              <tr v-if="order.order_title && order.type === OrderType.order">
                <th>
                  <small>{{ $t('customer') }}</small>
                </th>
                <td colspan="3">
                  <div class="text-break-word">{{ order.order_title }}</div>
                </td>
              </tr>
              <tr>
                <th>
                  <small>{{ $t('order-number') }}</small>
                </th>
                <td>
                  <div class="text-break-word">{{ order.order_number }}</div>
                </td>
                <th>
                  <small>{{ $t('date') }}</small>
                </th>
                <td>
                  <div class="text-nowrap">{{ order.created_at }}</div>
                </td>
              </tr>
              <tr>
                <th>
                  <small>{{ $t('type') }}</small>
                </th>
                <td colspan="3">
                  <div class="d-flex gap-12 flex-wrap">
                    <div>{{ parseType(order.type) }}</div>
                    <span :class="`badge badge--${parseBadgeClass(order.status)}`">
                      {{ parseStatus(order.status) }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="order.warehouse">
                <th>
                  <small>{{ $t('warehouse') }}</small>
                </th>
                <td colspan="3">
                  <div>{{ order.warehouse.title }}</div>
                </td>
              </tr>
              <tr>
                <th>
                  <small>{{ $t('note') }}</small>
                </th>
                <td colspan="3">
                  <div v-if="order.info" class="mb-8">{{ order.info }}</div>
                  <div>
                    <button class="order-comment" @click="openCommentModal">
                      {{ $t(order.info ? 'edit-order-comment' : 'add-order-comment') }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="items && items.length">
                <th>
                  <small class="text-nowrap">{{ $t('item-count') }}</small>
                </th>
                <td colspan="3">
                  <div v-if="showItems" class="d-flex flex-column gap-2 mb-12">
                    <div v-for="(item, itemIndex) in items" :key="itemIndex">
                      <div>
                        <b>{{ item.title }}</b>
                      </div>
                      <div>
                        <small
                          >{{ $t('code') }}:&nbsp;{{ item.erp_code }} | {{ $t('barcode') }}:&nbsp;{{
                            item.barcode.main
                          }}
                          | {{ $t('amount') }}:&nbsp;{{
                            `${item.quantity_collected}&nbsp;/&nbsp;${item.quantity_total}`
                          }}</small
                        >
                      </div>
                    </div>
                  </div>
                  <div>
                    <div v-if="totalItems !== null" class="d-inline pr-12">{{ totalItems }}</div>
                    <button
                      class="simple-link"
                      @click="
                        () => {
                          showItems = !showItems
                        }
                      "
                    >
                      {{ $t(showItems ? 'hide-items' : 'show-items') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="mt-auto d-flex flex-column gap-12">
        <AButton @click="goToItems">
          {{ $t(parseStartOperationLabel()) }}
        </AButton>
        <AButton class="btn-warning" @click="validateCurrentOrder()">
          {{ $t(parseCompleteOperationLabel()) }}
        </AButton>
        <RouterLink
          v-if="order.type === OrderType.order"
          :to="{ name: 'sale', params: { id: order.id } }"
          class="btn btn-warning py-12"
        >
          {{ $t('order-edit') }}
        </RouterLink>
        <AButton v-if="enableReservation" class="btn-warning" @click="validateCurrentOrder(true)">
          {{ $t('create-reservation') }}
        </AButton>
        <AButton
          v-if="enableCreateOffer"
          class="btn-warning"
          @click="validateCurrentOrder(false, true)"
        >
          {{ $t('create-offer') }}
        </AButton>
      </div>
    </template>
    <div ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <h4 class="mb-16">
              {{ $t(getModalHeader()) }}
            </h4>
            <p v-if="orderError && !reservationMode && !offerMode" class="mb-16 text-danger">
              <b>{{ orderError }}</b>
            </p>
            <p v-if="!orderError || reservationMode || offerMode" class="mb-32">
              {{ $t(getModalText()) }}
            </p>
            <div class="d-flex flex-wrap gap-12">
              <button
                type="button"
                class="btn btn-outline-secondary flex-1"
                data-bs-dismiss="modal"
              >
                {{ $t('cancel') }}
              </button>
              <button type="button" class="btn btn-danger flex-1" @click="completeCurrentOrder">
                {{ $t('finish-order-confirm') }}
              </button>
            </div>
            <div v-if="showRetry" class="pt-12">
              <button type="button" class="btn btn-danger w-100" @click="completeCurrentOrder">
                {{ $t('try-again') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div ref="commentModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-32 text-center">{{ $t('order-comment') }}</h4>
          <ATextarea v-model="comment" :label="$t('comment')" class="mb-32" />
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('close') }}
            </button>
            <button
              type="button"
              class="btn btn-primary flex-1"
              :disabled="isSubmitting"
              @click="updateComment"
            >
              {{ $t('save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
